import * as React from "react";
import { useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Stack,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  Button,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { Link } from "react-router-dom";

import heroImg from "../assets/blog2.png";

export default function BlogPostPage2() {
  const theme = useTheme();

  // ✅ SCROLL TO TOP (ONLY ADDITION)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const post = {
    title:
      "Most Of Hospitalized Patients May Suffer Fluid & Electrolyte Imbalance At Some Point Of There Stay",
    author: "Sudhanshu",
    date: "March 15, 2025",
    intro:
      "The percentage of hospitalized patients who experience fluid and electrolyte imbalances during their stay can vary widely depending on the patient population, underlying conditions, and the type of hospital. However, studies and clinical data suggest that fluid and electrolyte imbalances are common in hospitalized patients—particularly in critically ill patients, older adults, and those with chronic conditions like kidney disease, heart failure, or diabetes.",
    sections: [
      {
        title: "General Hospitalized Patients",
        bullets: [
          "Approximately 20–30% of hospitalized patients may experience some form of fluid or electrolyte imbalance during their stay.",
          "Common imbalances include hyponatremia (low sodium), hypernatremia (high sodium), hypokalemia (low potassium), and disturbances in calcium, magnesium, or phosphate levels.",
        ],
      },
      {
        title: "Critically Ill Patients (ICU Settings)",
        bullets: [
          "In intensive care units (ICUs), the prevalence is significantly higher, affecting 50–70% of patients.",
          "This is due to factors like sepsis, multi-organ failure, use of diuretics, and aggressive fluid resuscitation.",
        ],
      },
      {
        title: "Older Adults",
        bullets: [
          "Older adults (aged 65 and above) are at higher risk—studies suggest 30–50% may experience electrolyte disturbances.",
          "Common contributors include age-related decline in kidney function, polypharmacy, and comorbidities.",
        ],
      },
      {
        title: "Post-Surgical Patients",
        bullets: [
          "Post-operative patients are also at risk, with 20–40% experiencing fluid and electrolyte imbalances.",
          "Key reasons include blood loss, fluid shifts, and the stress response of surgery.",
        ],
      },
      {
        title: "Key Factors Contributing to Imbalances",
        bullets: [
          "Medications: diuretics, laxatives, and certain antibiotics can disrupt electrolyte levels.",
          "Chronic conditions: kidney disease, heart failure, liver disease, and diabetes increase risk.",
          "Acute illnesses: sepsis, burns, and GI losses (vomiting/diarrhea) can cause imbalances.",
          "Hospital interventions: IV fluids, blood transfusions, and nutritional support may contribute.",
        ],
      },
    ],
    conclusion:
      "While exact percentages vary, fluid and electrolyte imbalances are a significant concern in hospitalized patients—especially in high-risk groups. Proper monitoring and management are essential to prevent complications such as arrhythmias, neurological issues, and organ dysfunction.",
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f9fafb", py: { xs: 4, md: 6 } }}>
      <Container maxWidth="lg">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.2, sm: 3 },
            borderRadius: 4,
            border: "1px solid rgba(0,0,0,0.08)",
            boxShadow: "0 14px 40px rgba(0,0,0,0.06)",
            bgcolor: "#fff",
          }}
        >
          {/* HEADER */}
          <Stack spacing={1.5}>
            <Typography variant="h4" fontWeight={900}>
              {post.title}
            </Typography>

            <Stack direction="row" spacing={2} color="text.secondary">
              <Stack direction="row" spacing={0.6} alignItems="center">
                <PersonOutlineIcon fontSize="small" />
                <Typography variant="body2">{post.author}</Typography>
              </Stack>

              <Stack direction="row" spacing={0.6} alignItems="center">
                <CalendarMonthIcon fontSize="small" />
                <Typography variant="body2">{post.date}</Typography>
              </Stack>
            </Stack>
          </Stack>

          {/* IMAGE */}
          <Grid container spacing={2.5} sx={{ mt: 2 }}>
            <Grid item xs={12} md={3} />
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  width: "100%",
                  height: { xs: 240, sm: 300, md: 360 },
                  borderRadius: 3,
                  overflow: "hidden",
                  border: "1px solid rgba(0,0,0,0.1)",
                  boxShadow: "0 12px 30px rgba(0,0,0,0.08)",
                }}
              >
                <Box
                  component="img"
                  src={heroImg}
                  alt={post.title}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={3} />
          </Grid>

          {/* OVERVIEW */}
          <Box sx={{ mt: 2.5 }}>
            <Box
              sx={{
                p: 2.5,
                borderRadius: 3,
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 10px 26px rgba(0,0,0,0.05)",
              }}
            >
              <Typography variant="h6" fontWeight={900} mb={1}>
                Overview
              </Typography>

              <Typography
                variant="body1"
                sx={{ lineHeight: 1.9, color: "text.secondary" }}
              >
                {post.intro}
              </Typography>

              <Stack direction="row" flexWrap="wrap" gap={1} mt={2}>
                <Chip label="Hospitalized Patients" size="small" />
                <Chip label="ICU Risk" size="small" />
                <Chip label="Monitoring" size="small" />
              </Stack>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* CONTENT */}
          <Stack spacing={2.2}>
            {post.sections.map((sec, idx) => (
              <Box key={sec.title}>
                <Typography variant="h6" fontWeight={900} mb={1}>
                  {idx + 1}. {sec.title}
                </Typography>

                <List disablePadding>
                  {sec.bullets.map((b) => (
                    <ListItemButton
                      key={b}
                      disableRipple
                      sx={{
                        px: 0,
                        py: 0.6,
                        cursor: "default",
                        "&:hover": { bgcolor: "transparent" },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body1" sx={{ lineHeight: 1.85 }}>
                            • {b}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            ))}
          </Stack>

          <Divider sx={{ my: 3 }} />

          {/* CONCLUSION */}
          <Box
            sx={{
              p: 2.2,
              borderRadius: 3,
              bgcolor: alpha(theme.palette.primary.main, 0.06),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
            }}
          >
            <Typography variant="h6" fontWeight={900} mb={1}>
              Conclusion
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.9 }}>
              {post.conclusion}
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* PREVIOUS / NEXT */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Link to="/news" style={{ textDecoration: "none" }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ArrowBackRoundedIcon />}
                  sx={{
                    justifyContent: "flex-start",
                    borderRadius: 3,
                    py: 1.4,
                    fontWeight: 800,
                    textTransform: "none",
                  }}
                >
                  Previous Post
                </Button>
              </Link>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Button
                fullWidth
                variant="contained"
                endIcon={<ArrowForwardRoundedIcon />}
                sx={{
                  justifyContent: "flex-end",
                  borderRadius: 3,
                  py: 1.4,
                  fontWeight: 800,
                  textTransform: "none",
                }}
                component={Link}
                to="/BlogPostPage3"
              >
                Next Post
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
}
